import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Observable, Subscription } from 'rxjs';
import { DataQueryCoreFile } from '../../models/data-query-core-file.model';
import { DataQuery } from '../../models/data-query.model';
import { DataQueryService } from '../../data-query.service';
import { ErrorList } from '../../../../shared-components/controls/error-prompt/error-list.model';
import { DataQueryQuickStart } from '../../models/data-query-quick-start.model';
import { cloneDeep, isEqual } from 'lodash';
import { DataQueryCoreFileFilter } from '../../models/data-query-core-file-filter.model';
import { GeneralConfirmationComponent } from '../../../../shared-components/controls/general-confirmation/general-confirmation.component';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';

@AutoUnsubscribe()
@Component({
    selector: 'data-query-wizard-query-info',
    templateUrl: './data-query-wizard-query-info.component.html',
    styleUrls: [
        '../../data-query-shared-style.css',
        './data-query-wizard-query-info.component.css'
    ]
})
export class DataQueryWizardQueryInfoComponent implements OnInit, OnDestroy {

    // Interace Properties
    @Input() savedQueries: DataQuery[] = [];
    @Input() simpleValidationErrors: ErrorList[] = [];
    @Input() queryExists: boolean;

    // Data Properties
    coreFiles: DataQueryCoreFile[] = [];
    quickStarts: DataQueryQuickStart[] = [];
    selectedQuickStartId: number = null;
    coreFileFilters: DataQueryCoreFileFilter[] = [];
    selectedCoreFileFilterOptionId: number;

    // State Properties
    currentQuery: DataQuery = new DataQuery();
    subscriptions: Subscription[] = [];
    selectedCoreFileId: number;

    // View Properties
    @ViewChild('coreFileField', { read: ElementRef }) coreFileField: ElementRef;

    // Constructor
    constructor(
        private dataQueryService: DataQueryService,
        private dialog: MatDialog
    ) { }

    get quickStartValues(): number[] {
        return this.quickStarts.map(quickStart => quickStart ? quickStart.ID : null);
    }

    get quickStartLabels(): string[] {
        return this.quickStarts.map(quickStart => quickStart ? quickStart.Name : 'None');
    }

    get quickStartIds(): string[] {
        return this.quickStarts.map(quickStart => 'quickStartRadio' + (quickStart ? quickStart.ID : 'None'));
    }

    // Event Functions
    ngOnInit(): void {
        this.selectedCoreFileFilterOptionId = this.dataQueryService.selectedCoreFileFilterOptionId;
        // Getting state subscriptions
        this.subscriptions.push(
            this.dataQueryService.observableCurrentQueryStream$.subscribe(query => {
                this.currentQuery = query;
            }),
            this.dataQueryService.observableSelectedCoreFileStream$.subscribe(selectedCoreFile => {
                if (selectedCoreFile) {
                    this.selectedCoreFileId = selectedCoreFile.ID;
                    this.dataQueryService.getQuickStarts(this.selectedCoreFileId).subscribe(quickStarts => {
                        this.quickStarts = cloneDeep(quickStarts);
                        this.quickStarts.unshift(null);
                    });
                    this.dataQueryService.getCoreFileFilters(this.selectedCoreFileId).subscribe(coreFileFilters => {
                        this.coreFileFilters = coreFileFilters;
                    });
                }
            }),
            this.dataQueryService.observableSelectedQuickStartStream$.subscribe(selectedQuickStart => {
                const quickStart = this.quickStarts.find(q => q && q.ID == this.selectedQuickStartId);
                if (!isEqual(quickStart, selectedQuickStart)) {
                    this.selectedQuickStartId = selectedQuickStart ? selectedQuickStart.ID : null;
                }
            }),
        );

        // Getting data
        this.dataQueryService.getCoreFiles().subscribe(coreFiles => {
            this.coreFiles = coreFiles;
        });
    }

    ngOnDestroy(): void { }

    coreFileChange(coreFileId: number) {
        if (this.queryExists && this.dataQueryService.selectedCoreFile && this.dataQueryService.selectedCoreFile.ID != coreFileId) {
            // User has already started the query.  Show warning.
            this.showQueryChangeWarning().pipe(take(1)).subscribe(continueClicked => {
                if (continueClicked) {
                    this.queryExists = false;
                    this.selectCoreFile(coreFileId);
                }
                else {
                    // revert change if use clicks cancel
                    this.selectedCoreFileId = this.dataQueryService.selectedCoreFile ? this.dataQueryService.selectedCoreFile.ID: null;
                }
            });
        }
        else {
            this.selectCoreFile(coreFileId);
        }
    }

    // UI Functions
    selectCoreFile(coreFileId: number) {
        // PRE: the user changes the core file
        // POST: sets the core file to the selected core file
        if (!this.dataQueryService.selectedCoreFile || this.dataQueryService.selectedCoreFile.ID != coreFileId) {

            // Selecting a core file resets your query
            this.dataQueryService.currentQuery = new DataQuery();

            // Setting the selected core file globally and sending corresponding events
            this.dataQueryService.selectedCoreFile = this.coreFiles.find(coreFile => coreFile.ID == coreFileId);

            // set core file filter to default if core file changed
            const filter = this.coreFileFilters.find(o => o.CoreFileId == coreFileId);
            if (filter) {
                if (!filter.FilterOptions.find(o => o.ID == this.selectedCoreFileFilterOptionId)) {
                    const def = filter.FilterOptions.find(o => o.Default);
                    if (def) {
                        this.selectedCoreFileFilterOptionId = def.ID;
                    }
                    else {
                       this.selectedCoreFileFilterOptionId = null;
                    }
                }
            }
            else {
                this.selectedCoreFileFilterOptionId = null;
            }

            // clear quick start selection
            if (!this.quickStarts.find(o => o && o.CoreFileId == coreFileId && o.ID == this.selectedQuickStartId)) {
                this.selectQuickStart(null);
            }
        }
    }

    quickStartChange(quickStartId: number) {
        const quickStart = this.quickStarts.find(q => q && q.ID == quickStartId);
        if (this.queryExists && !isEqual(this.dataQueryService.selectedQuickStart, quickStart)) {
            // User has already started the query.  Show warning.
            this.showQueryChangeWarning().pipe(take(1)).subscribe(continueClicked => {
                if (continueClicked) {
                    this.queryExists = false;
                    this.selectQuickStart(quickStartId);
                }
                else {
                    // revert change if use clicks cancel
                    this.selectedQuickStartId = this.dataQueryService.selectedQuickStart ? this.dataQueryService.selectedQuickStart.ID : null;
                }
            });
        }
        else {
            this.selectQuickStart(quickStartId);
        }
    }

    selectQuickStart(quickStartId: number) {
        // PRE: the user changes the quick start
        // POST: sets the quick start to the selected quick start, then updates the query accordingly
        const quickStart = this.quickStarts.find(q => q && q.ID == quickStartId);
        if (!isEqual(this.dataQueryService.selectedQuickStart, quickStart)) {

            // Setting the query to the quick start query
            this.dataQueryService.selectedQuickStart = quickStart;
            this.selectedQuickStartId = quickStartId;
            if (quickStart) {
                this.dataQueryService.currentQuery = this.dataQueryService.getObjectFromXML(quickStart.QueryXML);
            } else {
                this.dataQueryService.currentQuery = null;
            }

            // Reapply filter portion of query
            this.selectFilterOption(this.selectedCoreFileFilterOptionId, true);
        }
    }

    filterOptionChange(filterOptionId: number, force = false) {
        if (this.queryExists && this.dataQueryService.selectedCoreFileFilterOptionId != filterOptionId) {
            // User has already started the query.  Show warning.
            this.showQueryChangeWarning().pipe(take(1)).subscribe(continueClicked => {
                if (continueClicked) {
                    this.queryExists = false;
                    this.selectFilterOption(filterOptionId, force);
                }
                else {
                    // revert change if use clicks cancel
                    this.selectedCoreFileFilterOptionId = filterOptionId;
                    setTimeout(() => {
                        this.selectedCoreFileFilterOptionId = this.dataQueryService.selectedCoreFileFilterOptionId;
                    }, 0);
                }
            });
        }
        else {
            this.selectFilterOption(filterOptionId, force);
        }
    }

    selectFilterOption(filterOptionId: number, force = false) {
        // PRE: the user changes a filter option selection
        // POST: sets the query up based on the filter option selected
        const filter = this.coreFileFilters.find(o => o.CoreFileId = this.selectedCoreFileId);
        if (filter) {
            const filterOption = filter.FilterOptions.find(o => o.ID == filterOptionId);
            if (
                filterOption
                && (
                    force
                    || this.selectedCoreFileFilterOptionId != filterOptionId
                )
            ) {

                // Removing the filters that were set by an option
                this.currentQuery.filterFields = this.currentQuery.filterFields.filter(filterField =>
                    filterField.coreFileFilterId && filterField.coreFileFilterId != filterOption.CoreFileFilterId
                );

                // Setting the filter based on the option
                this.selectedCoreFileFilterOptionId = filterOptionId;
                const filterQuery = this.dataQueryService.getObjectFromXML(filterOption.QueryXML);
                filterQuery.filterFields.forEach(filterField => {
                    filterField.coreFileFilterId = filterOption.CoreFileFilterId;
                    this.currentQuery.filterFields.push(filterField);
                });
                this.currentQuery.currentlyActiveStudentsOnly = filterQuery.currentlyActiveStudentsOnly;
                this.dataQueryService.selectedCoreFileFilterOptionId = filterOptionId;
            }
        }
    }

    showQueryChangeWarning(): Observable<any> {
        const warningDialog = this.dialog.open(GeneralConfirmationComponent, {
            panelClass: 'tyl-mat-dialog',
            disableClose: true,
            width: '530px'
          });
          warningDialog.componentInstance.headerLabel = 'WARNING';
          warningDialog.componentInstance.headerCenterAlign = true;
          warningDialog.componentInstance.confirmMessage = 'The options selected on this page will cause any existing field and filter selections to be overwritten.<br/><br/>If that is okay, select Continue.<br/><br/>If you do not want the existing selections overwritten, select Do Not Overwrite.';
          warningDialog.componentInstance.okButtonLabel = 'Continue';
          warningDialog.componentInstance.isDynamicCancelLable = 'Do Not Overwrite';
          warningDialog.componentInstance.okButtonImage = 'next arrow.png';
          return warningDialog.afterClosed();
    }
}
